import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  Activity,
  AlertTriangle,
  Boxes,
  Bug,
  CircleDot,
  Clock3,
  Cloud,
  Database,
  Eye,
  GitBranch,
  HardDrive,
  History,
  KeyRound,
  Layers3,
  Maximize2,
  Minimize2,
  Network,
  Radar,
  RefreshCw,
  RotateCcw,
  Server,
  ShieldCheck,
  ShieldEllipsis,
  TableProperties,
  UserRound,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';

interface FobNode {
  key: string;
  type: string;
  label: string;
  properties: Record<string, unknown>;
  riskScore: number;
  firstSeen: string | null;
  lastSeen: string | null;
  sourceTables: string[];
}

interface FobEdge {
  key: string;
  from: string;
  to: string;
  type: string;
  label: string;
  confidence: number;
  evidenceCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  sourceTables: string[];
}

interface FobSourceHealth {
  sourceKey: string;
  displayName: string;
  category: string;
  status: string;
  rowCount: number;
  firstSeen: string | null;
  lastSeen: string | null;
  latencyMs: number | null;
  message: string;
}

interface FobEvidence {
  id: string;
  sourceTable: string;
  evidenceType: string;
  evidenceKey: string;
  observedAt: string | null;
  queryName: string;
  payload: Record<string, unknown>;
}

interface FobSnapshot {
  id: string;
  customerId: string;
  customerName: string;
  lookbackHours: number;
  status: string;
  generatedAt: string | null;
  completedAt: string | null;
  summary: Record<string, unknown>;
  diagnostics: string[];
  createdBy?: string | null;
  nodes: FobNode[];
  edges: FobEdge[];
  sources: FobSourceHealth[];
}

interface FobSnapshotSummary {
  id: string;
  customerId: string;
  customerName: string;
  lookbackHours: number;
  status: string;
  generatedAt: string | null;
  completedAt: string | null;
  summary: Record<string, unknown>;
  diagnostics: string[];
}

interface AiC2FobOverlay {
  version: number;
  missionId: string;
  caseRef: string;
  customerId: string;
  snapshotId?: string | null;
  createdAt?: string;
  entities: Array<{ type: string; value: string; status: string }>;
  matchedNodeKeys: string[];
}

interface FobLakeSourceResult {
  sourceKey: string;
  displayName: string;
  status: 'healthy' | 'no_data' | 'unavailable' | 'error';
  rowCount: number;
  latencyMs: number | null;
  message: string;
}

interface FobLakeEnrichment {
  id: string;
  snapshotId: string;
  customerId: string;
  customerName: string;
  lookbackDays: number;
  baselineLookbackHours: number;
  status: string;
  generatedAt: string | null;
  completedAt: string | null;
  summary: Record<string, unknown>;
  nodes: FobNode[];
  edges: FobEdge[];
  evidence: FobEvidence[];
  sources: FobLakeSourceResult[];
  diagnostics: string[];
}

type DeltaNode = { key: string; type: string; label: string; riskScore: number };
type ThreatDelta = { current: number; previous: number; delta: number };
interface FobDelta {
  currentSnapshotId: string;
  previousSnapshotId: string | null;
  currentGeneratedAt: string | null;
  previousGeneratedAt: string | null;
  hasBaseline: boolean;
  nodes: { added: number; removed: number; net: number };
  edges: { added: number; removed: number; net: number };
  risk: { increased: number; decreased: number; topEscalations: Array<DeltaNode & { from: number; to: number; delta: number }> };
  threats: Record<string, ThreatDelta>;
  topAdded: DeltaNode[];
  topRemoved: DeltaNode[];
}

type ViewId = 'domain' | 'network' | 'identity' | 'endpoints' | 'controls' | 'threats' | 'timeline' | 'evidence' | 'sources';
type GraphViewId = Exclude<ViewId, 'timeline' | 'evidence' | 'sources'>;
type LayoutNode = FobNode & { x: number; y: number; width: number; height: number };

type LayoutResult = { nodes: LayoutNode[]; edges: FobEdge[]; clipped: number; width: number; height: number };

const VIEW_DEFS: Array<{ id: ViewId; label: string; capability: string; icon: typeof Network; description: string }> = [
  { id: 'domain', label: 'Domain Map', capability: 'view_domain_map', icon: Layers3, description: 'Tenant, cloud, workspace, controls and observed entities.' },
  { id: 'network', label: 'Network', capability: 'view_network', icon: Network, description: 'Network appliances, observed IP relationships and endpoint addresses.' },
  { id: 'identity', label: 'Identity', capability: 'view_identity', icon: UserRound, description: 'Entra identities and observed sign-in infrastructure.' },
  { id: 'endpoints', label: 'Endpoints', capability: 'view_endpoints', icon: HardDrive, description: 'Heartbeat and Defender-observed servers/endpoints.' },
  { id: 'controls', label: 'Security Controls', capability: 'view_security_controls', icon: ShieldCheck, description: 'Sentinel, Defender, Entra, AMA, TI and network controls.' },
  { id: 'threats', label: 'Active Threats', capability: 'view_active_threats', icon: AlertTriangle, description: 'Stage 1B overlay for incidents, alerts, IoCs, sightings, CVEs and risk-marked entities.' },
  { id: 'timeline', label: 'Timeline / Delta', capability: 'view_timeline_delta', icon: Clock3, description: 'Compare this terrain snapshot with its immediate predecessor.' },
  { id: 'evidence', label: 'Evidence', capability: 'view_evidence', icon: GitBranch, description: 'Source-backed evidence lineage for nodes and relationships.' },
  { id: 'sources', label: 'Source Health', capability: 'view_source_health', icon: Database, description: 'Table availability, freshness and terrain-scan source health.' },
];

const VIEW_TYPES: Record<GraphViewId, string[]> = {
  domain: ['customer', 'tenant', 'subscription', 'resource_group', 'workspace', 'security_control', 'network_device', 'endpoint', 'identity', 'ip', 'cloud_resource'],
  network: ['workspace', 'security_control', 'network_device', 'endpoint', 'ip', 'cloud_resource'],
  identity: ['tenant', 'identity', 'endpoint', 'ip', 'security_control'],
  endpoints: ['workspace', 'security_control', 'endpoint', 'ip', 'cloud_resource'],
  controls: ['workspace', 'security_control', 'network_device', 'data_source', 'endpoint'],
  threats: ['workspace', 'security_control', 'incident', 'alert', 'identity', 'endpoint', 'cloud_resource', 'ip', 'ioc', 'cve'],
};

const DEFAULT_LANE_ORDER = ['customer', 'tenant', 'subscription', 'resource_group', 'workspace', 'security_control', 'network_device', 'cloud_resource', 'endpoint', 'identity', 'ip', 'data_source'];
const THREAT_LANE_ORDER = ['workspace', 'incident', 'alert', 'identity', 'endpoint', 'cloud_resource', 'ip', 'ioc', 'cve', 'security_control'];

const IMMERSIVE_STARS = Array.from({ length: 110 }, (_, index) => ({
  x: 20 + ((index * 83) % 1370),
  y: 18 + ((index * 47) % 860),
  r: 0.5 + ((index * 11) % 7) / 8,
  opacity: 0.16 + ((index * 13) % 52) / 100,
}));

function mergeFobNodes(base: FobNode[], historical: FobNode[]): FobNode[] {
  const map = new Map(base.map((node) => [node.key, { ...node, properties: { ...node.properties }, sourceTables: [...node.sourceTables] }]));
  for (const node of historical) {
    const existing = map.get(node.key);
    if (!existing) {
      map.set(node.key, { ...node, properties: { ...node.properties, historicalOnly: true }, sourceTables: [...node.sourceTables] });
      continue;
    }
    existing.sourceTables = Array.from(new Set([...existing.sourceTables, ...node.sourceTables]));
    existing.properties = { ...node.properties, ...existing.properties, historicalContextAvailable: true };
    if (!existing.firstSeen || (node.firstSeen && new Date(node.firstSeen).getTime() < new Date(existing.firstSeen).getTime())) existing.firstSeen = node.firstSeen;
    if (!existing.lastSeen || (node.lastSeen && new Date(node.lastSeen).getTime() > new Date(existing.lastSeen).getTime())) existing.lastSeen = node.lastSeen;
  }
  return Array.from(map.values());
}

function mergeFobEdges(base: FobEdge[], historical: FobEdge[]): FobEdge[] {
  const map = new Map(base.map((edge) => [edge.key, { ...edge, sourceTables: [...edge.sourceTables] }]));
  for (const edge of historical) {
    const existing = map.get(edge.key);
    if (!existing) {
      map.set(edge.key, { ...edge, sourceTables: [...edge.sourceTables] });
      continue;
    }
    existing.sourceTables = Array.from(new Set([...existing.sourceTables, ...edge.sourceTables]));
    existing.evidenceCount = Math.max(existing.evidenceCount, edge.evidenceCount);
    existing.confidence = Math.max(existing.confidence, edge.confidence);
  }
  return Array.from(map.values());
}

function isHistoricalEdge(edge: FobEdge): boolean {
  return edge.type.startsWith('historically_') || edge.sourceTables.some((source) => source.startsWith('DataLake:'));
}


function apiErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? 'Unknown error');
}

function n(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : String(value);
}

function compact(value: number): string {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function signed(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}

function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (s === 'healthy' || s === 'succeeded') return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200';
  if (s === 'stale' || s === 'partial') return 'border-amber-400/30 bg-amber-500/10 text-amber-100';
  if (s === 'no_recent_data' || s === 'no_data') return 'border-slate-400/20 bg-slate-500/10 text-slate-300';
  return 'border-rose-400/30 bg-rose-500/10 text-rose-100';
}

function nodeTone(type: string): string {
  if (type === 'customer' || type === 'tenant') return 'text-violet-300';
  if (type === 'subscription' || type === 'resource_group' || type === 'cloud_resource') return 'text-sky-300';
  if (type === 'workspace') return 'text-cyan-300';
  if (type === 'security_control' || type === 'network_device') return 'text-emerald-300';
  if (type === 'endpoint') return 'text-blue-300';
  if (type === 'identity') return 'text-fuchsia-300';
  if (type === 'ip' || type === 'ioc') return 'text-amber-300';
  if (type === 'incident' || type === 'alert' || type === 'cve') return 'text-rose-300';
  return 'text-slate-300';
}

function nodeIcon(type: string) {
  if (type === 'identity') return UserRound;
  if (type === 'endpoint') return Server;
  if (type === 'ip' || type === 'network_device') return Network;
  if (type === 'security_control') return ShieldCheck;
  if (type === 'cloud_resource' || type === 'subscription' || type === 'resource_group') return Cloud;
  if (type === 'workspace') return Radar;
  if (type === 'data_source') return Database;
  if (type === 'cve') return Bug;
  if (type === 'incident' || type === 'alert') return AlertTriangle;
  return Boxes;
}

function safePropertyEntries(properties: Record<string, unknown>): Array<[string, string]> {
  return Object.entries(properties)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .slice(0, 18)
    .map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
}

function buildLayout(nodes: FobNode[], edges: FobEdge[], view: GraphViewId, expanded: boolean): LayoutResult {
  const allowed = new Set(VIEW_TYPES[view]);
  const candidates = nodes.filter((node) => allowed.has(node.type));
  const degree = new Map<string, number>();
  for (const edge of edges) {
    degree.set(edge.from, (degree.get(edge.from) ?? 0) + Math.min(edge.evidenceCount || 1, 25));
    degree.set(edge.to, (degree.get(edge.to) ?? 0) + Math.min(edge.evidenceCount || 1, 25));
  }
  const core = new Set(['customer', 'tenant', 'subscription', 'resource_group', 'workspace', 'security_control', 'network_device', 'incident', 'alert', 'ioc', 'cve']);
  const laneOrder = view === 'threats' ? THREAT_LANE_ORDER : DEFAULT_LANE_ORDER;
  const lanes = laneOrder.filter((type) => candidates.some((node) => node.type === type));
  const maxNodes = expanded ? (view === 'threats' ? 132 : 118) : (view === 'threats' ? 82 : 72);
  const perLane = Math.max(4, Math.floor(maxNodes / Math.max(1, lanes.length)));
  const selected: FobNode[] = [];

  for (const lane of lanes) {
    const laneNodes = candidates.filter((node) => node.type === lane).sort((a, b) => {
      const riskDiff = b.riskScore - a.riskScore;
      if (riskDiff) return riskDiff;
      const coreDiff = Number(core.has(b.type)) - Number(core.has(a.type));
      if (coreDiff) return coreDiff;
      const degreeDiff = (degree.get(b.key) ?? 0) - (degree.get(a.key) ?? 0);
      if (degreeDiff) return degreeDiff;
      return a.label.localeCompare(b.label);
    });
    selected.push(...laneNodes.slice(0, perLane));
  }

  const selectedKeys = new Set(selected.map((node) => node.key));
  const selectedEdges = edges
    .filter((edge) => selectedKeys.has(edge.from) && selectedKeys.has(edge.to))
    .sort((a, b) => {
      const threatA = Number(['observed_ioc', 'incident_contains_alert', 'alert_involves', 'alert_references_ioc', 'affected_by'].includes(a.type));
      const threatB = Number(['observed_ioc', 'incident_contains_alert', 'alert_involves', 'alert_references_ioc', 'affected_by'].includes(b.type));
      return threatB - threatA || b.evidenceCount - a.evidenceCount;
    })
    .slice(0, expanded ? 420 : 260);

  const width = expanded ? 1440 : 1180;
  const height = expanded ? 900 : 700;
  const marginX = expanded ? 100 : 82;
  const laneGap = lanes.length > 1 ? (width - marginX * 2) / (lanes.length - 1) : 0;
  const layout: LayoutNode[] = [];
  lanes.forEach((lane, laneIndex) => {
    const items = selected.filter((node) => node.type === lane);
    const usable = height - 130;
    const gap = items.length > 1 ? Math.max(50, Math.min(expanded ? 82 : 68, usable / (items.length - 1))) : 0;
    const blockHeight = gap * Math.max(0, items.length - 1);
    const startY = Math.max(62, (height - blockHeight) / 2);
    items.forEach((node, index) => {
      layout.push({ ...node, x: marginX + laneIndex * laneGap, y: startY + index * gap, width: expanded ? 146 : 132, height: expanded ? 46 : 42 });
    });
  });

  return { nodes: layout, edges: selectedEdges, clipped: Math.max(0, candidates.length - selected.length), width, height };
}

function nodeVisual(node: FobNode, selected: boolean, showRisk: boolean): { fill: string; stroke: string; strokeWidth: number } {
  if (selected) return { fill: 'rgba(8,145,178,.32)', stroke: 'rgb(34,211,238)', strokeWidth: 2.2 };
  if (showRisk && node.riskScore >= 90) return { fill: 'rgba(127,29,29,.48)', stroke: 'rgba(251,113,133,.95)', strokeWidth: 1.8 };
  if (showRisk && node.riskScore >= 70) return { fill: 'rgba(124,45,18,.42)', stroke: 'rgba(251,146,60,.9)', strokeWidth: 1.6 };
  if (showRisk && node.riskScore >= 45) return { fill: 'rgba(113,63,18,.34)', stroke: 'rgba(250,204,21,.75)', strokeWidth: 1.4 };
  if (node.type === 'incident' || node.type === 'alert') return { fill: 'rgba(76,5,25,.42)', stroke: 'rgba(244,63,94,.62)', strokeWidth: 1.3 };
  if (node.type === 'ioc' || node.type === 'cve') return { fill: 'rgba(69,26,3,.4)', stroke: 'rgba(245,158,11,.6)', strokeWidth: 1.2 };
  return { fill: 'rgba(15,23,42,.92)', stroke: 'rgba(148,163,184,.32)', strokeWidth: 1 };
}

function edgeVisual(edge: FobEdge, selected: boolean): { stroke: string; width: number; dash?: string } {
  if (selected) return { stroke: 'rgba(34,211,238,.98)', width: 2.6 };
  if (edge.type === 'observed_ioc') return { stroke: 'rgba(251,113,133,.95)', width: 2.5 };
  if (edge.type === 'incident_contains_alert' || edge.type === 'alert_involves' || edge.type === 'alert_references_ioc') return { stroke: 'rgba(244,63,94,.72)', width: 1.9 };
  if (edge.type === 'affected_by') return { stroke: 'rgba(249,115,22,.8)', width: 1.8 };
  if (edge.type === 'indicator_for' || edge.type === 'has_indicator') return { stroke: 'rgba(245,158,11,.65)', width: 1.5, dash: '7 5' };
  return { stroke: 'rgba(100,116,139,.38)', width: 1.1 };
}


function topologyEdgePath(from: LayoutNode, to: LayoutNode, immersive: boolean): string {
  if (!immersive) return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const normalX = -dy / distance;
  const normalY = dx / distance;
  const bend = Math.min(58, Math.max(12, distance * 0.08)) * (from.y <= to.y ? 1 : -1);
  const cx = (from.x + to.x) / 2 + normalX * bend;
  const cy = (from.y + to.y) / 2 + normalY * bend;
  return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
}

function isActiveThreatEdge(edge: FobEdge): boolean {
  return ['observed_ioc', 'incident_contains_alert', 'alert_involves', 'alert_references_ioc', 'affected_by'].includes(edge.type);
}

export function ForwardOperatingBaseTab() {
  const { customers, setActiveCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [lookbackHours, setLookbackHours] = useState(168);
  const [snapshot, setSnapshot] = useState<FobSnapshot | null>(null);
  const [snapshots, setSnapshots] = useState<FobSnapshotSummary[]>([]);
  const [snapshotChoice, setSnapshotChoice] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [activeView, setActiveView] = useState<ViewId>('domain');
  const [selectedNodeKey, setSelectedNodeKey] = useState('');
  const [nodeEvidence, setNodeEvidence] = useState<FobEvidence[]>([]);
  const [evidence, setEvidence] = useState<FobEvidence[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [delta, setDelta] = useState<FobDelta | null>(null);
  const [deltaLoading, setDeltaLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [graphZoom, setGraphZoom] = useState(1);
  const [showIocs, setShowIocs] = useState(true);
  const [showIncidents, setShowIncidents] = useState(true);
  const [showVulnerabilities, setShowVulnerabilities] = useState(true);
  const [immersiveTopology, setImmersiveTopology] = useState(false);
  const [graphPan, setGraphPan] = useState({ x: 0, y: 0 });
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterRelationship, setFilterRelationship] = useState('all');
  const [minRisk, setMinRisk] = useState(0);
  const [connectedOnly, setConnectedOnly] = useState(false);
  const [lakeEnrichment, setLakeEnrichment] = useState<FobLakeEnrichment | null>(null);
  const [lakeLookbackDays, setLakeLookbackDays] = useState(90);
  const [lakeLoading, setLakeLoading] = useState(false);
  const [showLakeOverlay, setShowLakeOverlay] = useState(true);
  const [aiC2Overlay, setAiC2Overlay] = useState<AiC2FobOverlay | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem('kloudsecure:ai-c2-fob-overlay');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as AiC2FobOverlay;
      return parsed && parsed.customerId && parsed.missionId ? parsed : null;
    } catch {
      return null;
    }
  });
  const aiC2OverlayAppliedRef = useRef(false);
  const graphCardRef = useRef<HTMLElement | null>(null);
  const panRef = useRef<{ active: boolean; pointerId: number; clientX: number; clientY: number; panX: number; panY: number }>({ active: false, pointerId: -1, clientX: 0, clientY: 0, panX: 0, panY: 0 });

  const visibleViews = useMemo(() => VIEW_DEFS.filter((view) => canCapability('forward_operating_base', view.capability)), [canCapability]);
  const canRun = canCapability('forward_operating_base', 'run_terrain_scan');
  const canRefresh = canCapability('forward_operating_base', 'refresh');
  const canViewSnapshots = canCapability('forward_operating_base', 'view_snapshots');
  const canViewEvidence = canCapability('forward_operating_base', 'view_evidence');
  const canViewIocs = canCapability('forward_operating_base', 'view_ioc_relationships');
  const canViewIncidentAlerts = canCapability('forward_operating_base', 'view_incident_alert_relationships');
  const canViewVulnerabilities = canCapability('forward_operating_base', 'view_vulnerability_overlays');
  const canViewRisk = canCapability('forward_operating_base', 'view_risk_indicators');
  const canViewDelta = canCapability('forward_operating_base', 'view_timeline_delta');
  const canImmersive = canCapability('forward_operating_base', 'use_immersive_topology');
  const canAdvancedFilters = canCapability('forward_operating_base', 'use_advanced_filters');
  const canViewLake = canCapability('forward_operating_base', 'view_data_lake_enrichment');
  const canEnrichLake = canCapability('forward_operating_base', 'enrich_data_lake');
  const canViewAiC2Overlay = canCapability('ai_command_control', 'view_fob_threat_overlay') && canCapability('forward_operating_base', 'view_active_threats');

  useEffect(() => {
    if (!visibleViews.some((view) => view.id === activeView)) setActiveView(visibleViews[0]?.id ?? 'domain');
  }, [activeView, visibleViews]);

  useEffect(() => {
    if (!canImmersive && immersiveTopology) setImmersiveTopology(false);
  }, [canImmersive, immersiveTopology]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === graphCardRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const isGraphView = activeView !== 'timeline' && activeView !== 'evidence' && activeView !== 'sources';
  const currentMapView: GraphViewId = isGraphView ? activeView : 'domain';

  useEffect(() => {
    if (!isGraphView && isFullscreen && document.fullscreenElement === graphCardRef.current) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }, [isGraphView, isFullscreen]);

  const combinedNodes = useMemo(() => {
    if (!snapshot) return [];
    return mergeFobNodes(snapshot.nodes, canViewLake && showLakeOverlay && lakeEnrichment ? lakeEnrichment.nodes : []);
  }, [snapshot, canViewLake, showLakeOverlay, lakeEnrichment]);

  const combinedEdges = useMemo(() => {
    if (!snapshot) return [];
    return mergeFobEdges(snapshot.edges, canViewLake && showLakeOverlay && lakeEnrichment ? lakeEnrichment.edges : []);
  }, [snapshot, canViewLake, showLakeOverlay, lakeEnrichment]);

  const aiC2OverlayNodeKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!aiC2Overlay || !canViewAiC2Overlay) return keys;
    for (const key of aiC2Overlay.matchedNodeKeys ?? []) if (key) keys.add(key);
    for (const entity of aiC2Overlay.entities ?? []) {
      const needle = String(entity.value ?? '').trim().toLowerCase();
      if (!needle || needle.length < 3) continue;
      for (const node of combinedNodes) {
        const haystack = `${node.label} ${node.type} ${JSON.stringify(node.properties)}`.toLowerCase();
        if (node.label.toLowerCase() === needle || haystack.includes(needle)) keys.add(node.key);
      }
    }
    return keys;
  }, [aiC2Overlay, canViewAiC2Overlay, combinedNodes]);

  const aiC2OverlayStatusByKey = useMemo(() => {
    const map = new Map<string, string>();
    if (!aiC2Overlay || !canViewAiC2Overlay) return map;
    for (const entity of aiC2Overlay.entities ?? []) {
      const needle = String(entity.value ?? '').trim().toLowerCase();
      if (!needle || needle.length < 3) continue;
      for (const node of combinedNodes) {
        const haystack = `${node.label} ${node.type} ${JSON.stringify(node.properties)}`.toLowerCase();
        if (node.label.toLowerCase() === needle || haystack.includes(needle)) map.set(node.key, String(entity.status ?? 'investigating'));
      }
    }
    return map;
  }, [aiC2Overlay, canViewAiC2Overlay, combinedNodes]);

  const selectedNode = useMemo(() => combinedNodes.find((node) => node.key === selectedNodeKey) ?? null, [combinedNodes, selectedNodeKey]);

  const typeOptions = useMemo(() => Array.from(new Set(combinedNodes.map((node) => node.type))).sort(), [combinedNodes]);
  const sourceOptions = useMemo(() => Array.from(new Set(combinedNodes.flatMap((node) => node.sourceTables))).sort(), [combinedNodes]);
  const relationshipOptions = useMemo(() => Array.from(new Set(combinedEdges.map((edge) => edge.type))).sort(), [combinedEdges]);

  const connectedKeys = useMemo(() => {
    if (!connectedOnly || !selectedNodeKey) return null;
    const keys = new Set<string>([selectedNodeKey]);
    for (const edge of combinedEdges) {
      if (edge.from === selectedNodeKey) keys.add(edge.to);
      if (edge.to === selectedNodeKey) keys.add(edge.from);
    }
    return keys;
  }, [connectedOnly, selectedNodeKey, combinedEdges]);

  const relationshipNodeKeys = useMemo(() => {
    if (filterRelationship === 'all') return null;
    const keys = new Set<string>();
    for (const edge of combinedEdges) {
      if (edge.type !== filterRelationship) continue;
      keys.add(edge.from);
      keys.add(edge.to);
    }
    return keys;
  }, [combinedEdges, filterRelationship]);

  const graphNodes = useMemo(() => {
    if (!snapshot) return [];
    const q = filterText.trim().toLowerCase();
    return combinedNodes.filter((node) => {
      if (currentMapView === 'threats') {
        if (node.type === 'ioc' && !(canViewIocs && showIocs)) return false;
        if ((node.type === 'incident' || node.type === 'alert') && !(canViewIncidentAlerts && showIncidents)) return false;
        if (node.type === 'cve' && !(canViewVulnerabilities && showVulnerabilities)) return false;
      }
      if (filterType !== 'all' && node.type !== filterType) return false;
      if (filterSource !== 'all' && !node.sourceTables.includes(filterSource)) return false;
      if (minRisk > 0 && node.riskScore < minRisk) return false;
      if (connectedKeys && !connectedKeys.has(node.key)) return false;
      if (relationshipNodeKeys && !relationshipNodeKeys.has(node.key)) return false;
      if (q) {
        const haystack = `${node.label} ${node.type} ${node.sourceTables.join(' ')} ${JSON.stringify(node.properties)}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [
    snapshot,
    combinedNodes,
    currentMapView,
    canViewIocs,
    showIocs,
    canViewIncidentAlerts,
    showIncidents,
    canViewVulnerabilities,
    showVulnerabilities,
    filterText,
    filterType,
    filterSource,
    minRisk,
    connectedKeys,
    relationshipNodeKeys,
  ]);

  const graphNodeKeys = useMemo(() => new Set(graphNodes.map((node) => node.key)), [graphNodes]);
  const graphEdges = useMemo(() => combinedEdges.filter((edge) => {
    if (!graphNodeKeys.has(edge.from) || !graphNodeKeys.has(edge.to)) return false;
    if (filterRelationship !== 'all' && edge.type !== filterRelationship) return false;
    return true;
  }), [combinedEdges, graphNodeKeys, filterRelationship]);

  const layout = useMemo(
    () => snapshot ? buildLayout(graphNodes, graphEdges, currentMapView, isFullscreen) : { nodes: [], edges: [], clipped: 0, width: 1180, height: 700 },
    [snapshot, graphNodes, graphEdges, currentMapView, isFullscreen],
  );
  const positionByKey = useMemo(() => new Map(layout.nodes.map((node) => [node.key, node])), [layout.nodes]);
  const visibleEdges = useMemo(
    () => layout.edges.map((edge) => ({ edge, from: positionByKey.get(edge.from), to: positionByKey.get(edge.to) })).filter((row) => row.from && row.to) as Array<{ edge: FobEdge; from: LayoutNode; to: LayoutNode }>,
    [layout.edges, positionByKey],
  );

  const viewBox = useMemo(() => {
    const width = layout.width / graphZoom;
    const height = layout.height / graphZoom;
    const x = (layout.width - width) / 2 - graphPan.x;
    const y = (layout.height - height) / 2 - graphPan.y;
    return `${x} ${y} ${width} ${height}`;
  }, [layout.width, layout.height, graphZoom, graphPan]);

  const historicalSnapshotIndex = useMemo(() => snapshots.findIndex((item) => item.id === snapshot?.id), [snapshots, snapshot?.id]);
  const isHistoricalSnapshot = historicalSnapshotIndex > 0;

  const loadSnapshotHistory = useCallback(async (customerId: string) => {
    if (!customerId || !canViewSnapshots) return;
    try {
      const data = await apiFetch<{ snapshots?: FobSnapshotSummary[] }>(`/forward-operating-base/snapshots?customerId=${encodeURIComponent(customerId)}`);
      setSnapshots(Array.isArray(data.snapshots) ? data.snapshots : []);
    } catch {
      setSnapshots([]);
    }
  }, [canViewSnapshots]);


  const loadLakeEnrichment = useCallback(async (customerId: string, snapshotId: string) => {
    setLakeEnrichment(null);
    if (!customerId || !snapshotId || !canViewLake) return;
    try {
      const data = await apiFetch<{ enrichment?: FobLakeEnrichment | null }>(
        `/forward-operating-base/datalake-enrichment?customerId=${encodeURIComponent(customerId)}&snapshotId=${encodeURIComponent(snapshotId)}`,
      );
      setLakeEnrichment(data.enrichment ?? null);
    } catch {
      setLakeEnrichment(null);
    }
  }, [canViewLake]);

  const resetViewport = useCallback(() => {
    setGraphZoom(1);
    setGraphPan({ x: 0, y: 0 });
  }, []);

  const clearAdvancedFilters = useCallback(() => {
    setFilterText('');
    setFilterType('all');
    setFilterSource('all');
    setFilterRelationship('all');
    setMinRisk(0);
    setConnectedOnly(false);
  }, []);

  const loadLatest = useCallback(async (customerId: string) => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const data = await apiFetch<{ snapshot?: FobSnapshot | null }>(`/forward-operating-base/latest?customerId=${encodeURIComponent(customerId)}`);
      setSnapshot(data.snapshot ?? null);
      setSelectedNodeKey('');
      setNodeEvidence([]);
      setEvidence([]);
      setDelta(null);
      resetViewport();
      clearAdvancedFilters();
      if (data.snapshot) await loadLakeEnrichment(customerId, data.snapshot.id);
      else {
        setLakeEnrichment(null);
        setNotice('No Forward Operating Base snapshot exists for this customer yet. Run a Customer Terrain Scan to establish the first operational picture.');
      }
      await loadSnapshotHistory(customerId);
    } catch (e) {
      setError(apiErrorMessage(e));
      setSnapshot(null);
      setLakeEnrichment(null);
    } finally {
      setLoading(false);
    }
  }, [loadSnapshotHistory, loadLakeEnrichment, resetViewport, clearAdvancedFilters]);

  async function chooseCustomer(customerId: string) {
    setSelectedCustomerId(customerId);
    setSnapshot(null);
    setSnapshots([]);
    setSnapshotChoice('');
    setSelectedNodeKey('');
    setNodeEvidence([]);
    setEvidence([]);
    setDelta(null);
    setLakeEnrichment(null);
    resetViewport();
    clearAdvancedFilters();
    setError('');
    setNotice('');
    if (!customerId) return;
    setActiveCustomerId(customerId);
    await loadLatest(customerId);
  }

  async function runTerrainScan() {
    if (!selectedCustomerId || (snapshot ? !canRefresh : !canRun)) return;
    setScanning(true);
    setError('');
    setNotice('');
    try {
      const endpoint = snapshot ? '/forward-operating-base/refresh' : '/forward-operating-base/scan';
      const data = await apiFetch<{ snapshot?: FobSnapshot | null; durationMs?: number }>(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: selectedCustomerId, lookbackHours }),
      });
      if (!data.snapshot) throw new Error('Terrain scan completed without a stored snapshot.');
      setSnapshot(data.snapshot);
      setSelectedNodeKey('');
      setNodeEvidence([]);
      setEvidence([]);
      setDelta(null);
      setLakeEnrichment(null);
      resetViewport();
      setNotice(`Terrain snapshot completed in ${Math.max(1, Math.round((data.durationMs ?? 0) / 1000))}s. Stage 1C refreshed the read-only operational picture; historical Data Lake enrichment remains an explicit, separately controlled action.`);
      await loadSnapshotHistory(selectedCustomerId);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setScanning(false);
    }
  }

  async function loadSnapshotById(snapshotId: string, message = 'Historical Forward Operating Base snapshot loaded.') {
    if (!selectedCustomerId || !snapshotId) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch<{ snapshot?: FobSnapshot | null }>(`/forward-operating-base/snapshot/${encodeURIComponent(snapshotId)}?customerId=${encodeURIComponent(selectedCustomerId)}`);
      if (!data.snapshot) throw new Error('Snapshot not found.');
      setSnapshot(data.snapshot);
      setSnapshotChoice(snapshotId);
      setSelectedNodeKey('');
      setNodeEvidence([]);
      setEvidence([]);
      setDelta(null);
      resetViewport();
      clearAdvancedFilters();
      await loadLakeEnrichment(selectedCustomerId, snapshotId);
      setNotice(message);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadHistoricalSnapshot() {
    if (!snapshotChoice) return;
    await loadSnapshotById(snapshotChoice);
  }

  async function navigateSnapshot(direction: 'older' | 'newer') {
    if (!snapshot || historicalSnapshotIndex < 0) return;
    const nextIndex = direction === 'older' ? historicalSnapshotIndex + 1 : historicalSnapshotIndex - 1;
    const target = snapshots[nextIndex];
    if (!target) return;
    await loadSnapshotById(target.id, `${direction === 'older' ? 'Older' : 'Newer'} FOB snapshot loaded for time-travel analysis.`);
  }

  async function runLakeEnrichment() {
    if (!selectedCustomerId || !snapshot?.id || !canEnrichLake) return;
    setLakeLoading(true);
    setError('');
    try {
      const data = await apiFetch<{ enrichment?: FobLakeEnrichment | null; durationMs?: number }>('/forward-operating-base/datalake-enrich', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: selectedCustomerId, snapshotId: snapshot.id, lookbackDays: lakeLookbackDays }),
      });
      if (!data.enrichment) throw new Error('Data Lake enrichment completed without a persisted result.');
      setLakeEnrichment(data.enrichment);
      setShowLakeOverlay(true);
      setNotice(`Historical Sentinel Data Lake enrichment completed in ${Math.max(1, Math.round((data.durationMs ?? 0) / 1000))}s. The overlay is read-only, bounded and persisted separately from the immutable FOB snapshot.`);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLakeLoading(false);
    }
  }

  const loadEvidence = useCallback(async (evidenceKey?: string) => {
    if (!selectedCustomerId || !snapshot?.id || !canViewEvidence) return;
    setEvidenceLoading(true);
    try {
      const params = new URLSearchParams({ customerId: selectedCustomerId, snapshotId: snapshot.id });
      if (evidenceKey) params.set('evidenceKey', evidenceKey);
      const data = await apiFetch<{ evidence?: FobEvidence[] }>(`/forward-operating-base/evidence?${params.toString()}`);
      const rows = Array.isArray(data.evidence) ? data.evidence : [];
      const lakeRows = canViewLake && lakeEnrichment
        ? lakeEnrichment.evidence.filter((item) => !evidenceKey || item.evidenceKey === evidenceKey)
        : [];
      const merged = [...rows, ...lakeRows].sort((a, b) => new Date(b.observedAt ?? 0).getTime() - new Date(a.observedAt ?? 0).getTime());
      if (evidenceKey) setNodeEvidence(merged); else setEvidence(merged);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setEvidenceLoading(false);
    }
  }, [selectedCustomerId, snapshot?.id, canViewEvidence, canViewLake, lakeEnrichment]);

  const loadDelta = useCallback(async () => {
    if (!selectedCustomerId || !snapshot?.id || !canViewDelta) return;
    setDeltaLoading(true);
    try {
      const data = await apiFetch<{ delta?: FobDelta | null }>(`/forward-operating-base/delta?customerId=${encodeURIComponent(selectedCustomerId)}&snapshotId=${encodeURIComponent(snapshot.id)}`);
      setDelta(data.delta ?? null);
    } catch (e) {
      setError(apiErrorMessage(e));
      setDelta(null);
    } finally {
      setDeltaLoading(false);
    }
  }, [selectedCustomerId, snapshot?.id, canViewDelta]);

  useEffect(() => {
    if (!aiC2Overlay || !canViewAiC2Overlay || aiC2OverlayAppliedRef.current) return;
    if (!customers.some((customer) => customer.id === aiC2Overlay.customerId)) {
      sessionStorage.removeItem('kloudsecure:ai-c2-fob-overlay');
      setAiC2Overlay(null);
      return;
    }
    aiC2OverlayAppliedRef.current = true;
    setActiveView('threats');
    setShowIocs(true);
    setShowIncidents(true);
    setShowVulnerabilities(true);
    if (canImmersive) setImmersiveTopology(true);
    void (async () => {
      await chooseCustomer(aiC2Overlay.customerId);
      if (aiC2Overlay.snapshotId) {
        await loadSnapshotById(aiC2Overlay.snapshotId, `AI C2 ${aiC2Overlay.caseRef} linked Forward Operating Base snapshot loaded.`);
      }
    })();
  // The overlay is a one-shot handoff from AI C2; function declarations are stable for this mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiC2Overlay?.missionId, canViewAiC2Overlay, canImmersive, customers]);

  useEffect(() => {
    if (activeView === 'evidence' && snapshot && evidence.length === 0 && canViewEvidence) void loadEvidence();
  }, [activeView, snapshot, evidence.length, canViewEvidence, loadEvidence]);

  useEffect(() => {
    if (activeView === 'timeline' && snapshot && canViewDelta) void loadDelta();
  }, [activeView, snapshot?.id, canViewDelta, loadDelta]);

  async function selectNode(key: string) {
    setSelectedNodeKey(key);
    setNodeEvidence([]);
    if (canViewEvidence) await loadEvidence(key);
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement === graphCardRef.current) await document.exitFullscreen();
      else await graphCardRef.current?.requestFullscreen();
    } catch (e) {
      setError(`Fullscreen could not be changed: ${apiErrorMessage(e)}`);
    }
  }


  function onGraphPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    const target = event.target as Element | null;
    if (target?.closest('[data-fob-node="true"]')) return;
    panRef.current = {
      active: true,
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      panX: graphPan.x,
      panY: graphPan.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onGraphPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    const state = panRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const viewWidth = layout.width / graphZoom;
    const viewHeight = layout.height / graphZoom;
    const scaleX = rect.width > 0 ? viewWidth / rect.width : 1;
    const scaleY = rect.height > 0 ? viewHeight / rect.height : 1;
    setGraphPan({
      x: state.panX + (event.clientX - state.clientX) * scaleX,
      y: state.panY + (event.clientY - state.clientY) * scaleY,
    });
  }

  function onGraphPointerUp(event: ReactPointerEvent<SVGSVGElement>) {
    if (panRef.current.pointerId !== event.pointerId) return;
    panRef.current.active = false;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* pointer capture may already be released */ }
  }

  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);
  const summary = snapshot?.summary ?? {};
  const sourceHealthy = snapshot?.sources.filter((source) => source.status === 'healthy').length ?? 0;
  const availableSources = snapshot?.sources.filter((source) => source.status === 'healthy' || source.status === 'stale').length ?? 0;

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-gradient-to-r from-slate-950 via-slate-950 to-cyan-950/40 px-5 py-5 text-slate-100">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300"><Radar size={15} /> Forward Operating Base · Stage 1C</div>
              <h2 className="mt-2 text-2xl font-black">Customer security terrain + immersive historical operating picture</h2>
              <p className="mt-2 text-sm leading-6 text-slate-300">Read-only operational terrain with active threat overlays, immersive/pannable topology, advanced filtering, historical snapshot time travel and optional Microsoft Sentinel Data Lake enrichment.</p>
            </div>
            <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100"><ShieldEllipsis size={14} className="mr-2 inline" /> No containment actions in Stage 1C</div>
          </div>
        </div>

        <div className="grid gap-4 p-5 xl:grid-cols-[1.3fr_0.7fr]">
          <div className="rounded-xl border border-border bg-background p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Mandatory customer context</div>
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_auto]">
              <label className="text-xs font-semibold text-muted-foreground">Customer
                <select value={selectedCustomerId} onChange={(e) => void chooseCustomer(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
                  <option value="">Select customer explicitly…</option>
                  {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                </select>
              </label>
              <label className="text-xs font-semibold text-muted-foreground">Terrain window
                <select value={lookbackHours} onChange={(e) => setLookbackHours(Number(e.target.value))} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
                  <option value={24}>24 hours</option>
                  <option value={168}>7 days</option>
                  <option value={720}>30 days</option>
                </select>
              </label>
              <div className="flex items-end gap-2">
                <button type="button" onClick={() => void runTerrainScan()} disabled={!selectedCustomerId || scanning || (snapshot ? !canRefresh : !canRun)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-40"><RefreshCw size={15} className={scanning ? 'animate-spin' : ''} />{scanning ? 'Scanning terrain…' : snapshot ? 'Refresh terrain' : 'Run terrain scan'}</button>
              </div>
            </div>
            {selectedCustomer ? <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-4"><span>Tenant <b className="text-foreground">{selectedCustomer.tenantId || '—'}</b></span><span>Workspace <b className="text-foreground">{selectedCustomer.workspaceName || selectedCustomer.workspaceId || '—'}</b></span><span>Subscription <b className="text-foreground">{selectedCustomer.subscription || '—'}</b></span><span>Access <b className="text-foreground">{selectedCustomer.lighthouseMode ? 'Azure Lighthouse' : 'Customer SPN'}</b></span></div> : null}
          </div>

          <div className="rounded-xl border border-border bg-background p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><History size={14} /> Historical operating picture</div>
              {isHistoricalSnapshot ? <span className="rounded-full border border-violet-400/30 bg-violet-500/10 px-2 py-1 text-[10px] font-black uppercase text-violet-200">Time travel</span> : null}
            </div>
            {canViewSnapshots ? <>
              <div className="mt-3 flex gap-2">
                <select value={snapshotChoice} onChange={(e) => setSnapshotChoice(e.target.value)} disabled={!selectedCustomerId || snapshots.length === 0} className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground">
                  <option value="">Recent snapshots…</option>
                  {snapshots.map((item) => <option key={item.id} value={item.id}>{formatTime(item.generatedAt)} · {item.status} · {n(item.summary.nodes)} nodes</option>)}
                </select>
                <button type="button" onClick={() => void loadHistoricalSnapshot()} disabled={!snapshotChoice || loading} className="rounded-lg border border-border px-3 py-2 text-xs font-bold hover:bg-muted disabled:opacity-40">Load</button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => void navigateSnapshot('older')} disabled={historicalSnapshotIndex < 0 || historicalSnapshotIndex >= snapshots.length - 1 || loading} className="rounded-lg border border-border px-3 py-2 text-xs font-bold hover:bg-muted disabled:opacity-35">← Older</button>
                <button type="button" onClick={() => void navigateSnapshot('newer')} disabled={historicalSnapshotIndex <= 0 || loading} className="rounded-lg border border-border px-3 py-2 text-xs font-bold hover:bg-muted disabled:opacity-35">Newer →</button>
              </div>
              <div className="mt-3 text-[11px] text-muted-foreground">Latest 30 snapshots are retained. Older/Newer navigation turns the FOB into a persisted time-travel view without mutating historical evidence.</div>
            </> : <div className="mt-3 text-xs text-amber-300">Snapshot history is hidden by Permissions Master.</div>}
          </div>
        </div>
      </section>

      {error ? <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-100">{error}</div> : null}
      {notice ? <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/10 p-4 text-sm text-cyan-100">{notice}</div> : null}
      {loading ? <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground"><RefreshCw size={16} className="mr-2 inline animate-spin" />Loading Forward Operating Base snapshot…</div> : null}

      {!selectedCustomerId ? (
        <div className="rounded-2xl border border-dashed border-cyan-400/30 bg-cyan-500/5 p-10 text-center">
          <KeyRound size={34} className="mx-auto text-cyan-300" />
          <div className="mt-3 text-lg font-black text-foreground">Select a customer explicitly</div>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">Forward Operating Base never falls back to the Kloudynet/default tenant. Customer selection is required before terrain or historical snapshots can be read.</p>
        </div>
      ) : null}

      {snapshot ? <>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <Metric label="Nodes" value={compact(n(summary.nodes))} helper={`${n(summary.endpoints)} endpoints`} icon={CircleDot} />
          <Metric label="Relationships" value={compact(n(summary.edges))} helper="evidence-backed edges" icon={GitBranch} />
          <Metric label="Identities" value={compact(n(summary.identities))} helper="Entra-observed" icon={UserRound} />
          <Metric label="Network devices" value={compact(n(summary.networkDevices))} helper={`${n(summary.ips)} IP nodes`} icon={Network} />
          <Metric label="Cloud resources" value={compact(n(summary.cloudResources))} helper="AzureActivity + resource IDs" icon={Cloud} />
          <Metric label="Healthy sources" value={`${sourceHealthy}/${snapshot.sources.length}`} helper={`${availableSources} available incl. stale`} icon={Activity} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <Metric label="Open incidents" value={compact(n(summary.incidents))} helper="SecurityIncident overlay" icon={AlertTriangle} />
          <Metric label="Security alerts" value={compact(n(summary.alerts))} helper="recent source-backed alerts" icon={Activity} />
          <Metric label="Active IoCs" value={compact(n(summary.iocs))} helper={`${n(summary.iocSightings)} observed sighting links`} icon={Radar} />
          <Metric label="CVE exposure" value={compact(n(summary.cves))} helper={`${n(summary.vulnerableDevices)} vulnerable devices`} icon={Bug} />
          <Metric label="High-risk nodes" value={canViewRisk ? compact(n(summary.highRiskNodes)) : 'Hidden'} helper={canViewRisk ? `${n(summary.criticalRiskNodes)} critical-risk nodes` : 'Permissions Master'} icon={AlertTriangle} />
          <Metric label="Terrain model" value="1C" helper="immersive + historical context" icon={ShieldCheck} />
        </div>

        <section ref={graphCardRef} className={`overflow-hidden border border-border bg-card shadow-sm ${isFullscreen ? 'flex h-screen w-screen flex-col rounded-none bg-background' : 'rounded-2xl'}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div>
              <div className="text-sm font-black text-foreground">Operational terrain view</div>
              <div className="text-xs text-muted-foreground">Snapshot {snapshot.id.slice(0, 8)} · {formatTime(snapshot.generatedAt)} · {snapshot.lookbackHours}h · <span className={`rounded-full border px-2 py-0.5 ${statusTone(snapshot.status)}`}>{snapshot.status}</span></div>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {visibleViews.map((view) => {
                const Icon = view.icon;
                return <button key={view.id} type="button" title={view.description} onClick={() => { setActiveView(view.id); resetViewport(); }} className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-bold ${activeView === view.id ? 'bg-primary text-primary-foreground' : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}><Icon size={13} />{view.label}</button>;
              })}
              {isGraphView ? <div className="ml-1 flex items-center gap-1 border-l border-border pl-2">
                {canImmersive ? <div className="mr-1 flex items-center rounded-lg border border-border bg-background p-0.5">
                  <button type="button" onClick={() => setImmersiveTopology(false)} className={`rounded-md px-2.5 py-1.5 text-[10px] font-black ${!immersiveTopology ? 'bg-slate-700 text-white' : 'text-muted-foreground hover:text-foreground'}`}>Standard</button>
                  <button type="button" onClick={() => setImmersiveTopology(true)} className={`rounded-md px-2.5 py-1.5 text-[10px] font-black ${immersiveTopology ? 'bg-violet-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}>Immersive</button>
                </div> : null}
                <button type="button" title="Zoom out" onClick={() => setGraphZoom((value) => Math.max(0.5, Number((value - 0.15).toFixed(2))))} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"><ZoomOut size={14} /></button>
                <button type="button" title="Zoom in" onClick={() => setGraphZoom((value) => Math.min(2.4, Number((value + 0.15).toFixed(2))))} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"><ZoomIn size={14} /></button>
                <button type="button" title="Reset zoom and canvas position" onClick={resetViewport} className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted hover:text-foreground"><RotateCcw size={14} /></button>
                <button type="button" onClick={() => void toggleFullscreen()} className="inline-flex items-center gap-1 rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-black text-cyan-100 hover:bg-cyan-500/20">{isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}{isFullscreen ? 'Exit full screen' : 'Maximize'}</button>
              </div> : null}
            </div>
          </div>

          {aiC2Overlay && canViewAiC2Overlay ? <div className="flex flex-wrap items-center gap-2 border-b border-fuchsia-400/20 bg-fuchsia-950/20 px-4 py-2 text-xs">
            <span className="inline-flex items-center gap-1.5 font-black uppercase tracking-wide text-fuchsia-200"><Radar size={13} /> AI C2 Active Threat Overlay</span>
            <span className="text-fuchsia-100">{aiC2Overlay.caseRef}</span>
            <span className="text-muted-foreground">{aiC2OverlayNodeKeys.size} terrain node(s) illuminated from {aiC2Overlay.entities.length} mission entities</span>
            {aiC2Overlay.snapshotId && snapshot.id !== aiC2Overlay.snapshotId ? <span className="rounded border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-200">Viewing a different snapshot than the AI C2 case</span> : null}
            <button type="button" onClick={() => { sessionStorage.removeItem('kloudsecure:ai-c2-fob-overlay'); setAiC2Overlay(null); }} className="ml-auto rounded-lg border border-fuchsia-400/25 px-2.5 py-1 text-[10px] font-bold text-fuchsia-200 hover:bg-fuchsia-500/10">Clear AI C2 overlay</button>
          </div> : null}

          {activeView === 'threats' ? <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/20 px-4 py-2 text-xs">
            <span className="font-black uppercase tracking-wide text-muted-foreground">Threat layers</span>
            {canViewIncidentAlerts ? <LayerToggle label="Incidents + alerts" active={showIncidents} onClick={() => setShowIncidents((value) => !value)} /> : null}
            {canViewIocs ? <LayerToggle label="IoCs + sightings" active={showIocs} onClick={() => setShowIocs((value) => !value)} /> : null}
            {canViewVulnerabilities ? <LayerToggle label="MDE vulnerabilities" active={showVulnerabilities} onClick={() => setShowVulnerabilities((value) => !value)} /> : null}
            <span className="ml-auto text-muted-foreground">Solid red/orange edges = observed threat/vulnerability relationships · dashed amber = TI context</span>
          </div> : null}

          {isGraphView && canViewLake ? <div className="flex flex-wrap items-center gap-2 border-b border-border bg-cyan-950/15 px-4 py-2 text-xs">
            <span className="font-black uppercase tracking-wide text-cyan-200">Historical Data Lake</span>
            <select value={lakeLookbackDays} onChange={(e) => setLakeLookbackDays(Number(e.target.value))} className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs text-foreground">
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days</option>
            </select>
            {canEnrichLake ? <button type="button" onClick={() => void runLakeEnrichment()} disabled={lakeLoading} className="inline-flex items-center gap-1 rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 font-black text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-40"><Database size={13} className={lakeLoading ? 'animate-spin' : ''} />{lakeLoading ? 'Enriching…' : 'Enrich from Data Lake'}</button> : null}
            {lakeEnrichment ? <LayerToggle label={`Historical overlay · ${lakeEnrichment.lookbackDays}d`} active={showLakeOverlay} onClick={() => setShowLakeOverlay((value) => !value)} /> : <span className="text-muted-foreground">No persisted lake enrichment for this snapshot.</span>}
            {lakeEnrichment ? <span className="text-muted-foreground">{n(lakeEnrichment.summary.nodes)} historical nodes · {n(lakeEnrichment.summary.edges)} edges · {lakeEnrichment.sources.filter((source) => source.status === 'healthy' || source.status === 'no_data').length}/{lakeEnrichment.sources.length} sources queryable</span> : null}
            <span className="ml-auto text-[10px] text-muted-foreground">Read-only API query · historical context only · Data Lake query charges may apply</span>
          </div> : null}

          {isGraphView && canAdvancedFilters ? <div className="border-b border-border bg-muted/15 px-4 py-3">
            <div className="grid gap-2 xl:grid-cols-[minmax(180px,1.3fr)_150px_190px_190px_130px_auto_auto]">
              <input value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Search node label, property or source…" className="min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground outline-none focus:border-cyan-400/60" />
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground">
                <option value="all">All node types</option>
                {typeOptions.map((type) => <option key={type} value={type}>{type.replaceAll('_', ' ')}</option>)}
              </select>
              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground">
                <option value="all">All sources</option>
                {sourceOptions.map((source) => <option key={source} value={source}>{source}</option>)}
              </select>
              <select value={filterRelationship} onChange={(e) => setFilterRelationship(e.target.value)} className="rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground">
                <option value="all">All relationships</option>
                {relationshipOptions.map((type) => <option key={type} value={type}>{type.replaceAll('_', ' ')}</option>)}
              </select>
              <select value={minRisk} onChange={(e) => setMinRisk(Number(e.target.value))} className="rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground">
                <option value={0}>Any risk</option>
                <option value={45}>Risk ≥ 45</option>
                <option value={70}>Risk ≥ 70</option>
                <option value={90}>Risk ≥ 90</option>
              </select>
              <button type="button" disabled={!selectedNodeKey} onClick={() => setConnectedOnly((value) => !value)} className={`rounded-lg border px-3 py-2 text-xs font-bold disabled:opacity-35 ${connectedOnly ? 'border-violet-400/40 bg-violet-500/15 text-violet-100' : 'border-border text-muted-foreground hover:bg-muted'}`}>Neighborhood</button>
              <button type="button" onClick={clearAdvancedFilters} className="rounded-lg border border-border px-3 py-2 text-xs font-bold text-muted-foreground hover:bg-muted hover:text-foreground">Clear</button>
            </div>
            <div className="mt-2 text-[10px] text-muted-foreground">Advanced filters are client-side and never change the persisted snapshot. Neighborhood shows the selected node plus its directly connected entities.</div>
          </div> : null}

          {isGraphView ? (
            <div className={`grid ${isFullscreen ? 'min-h-0 flex-1 grid-cols-[minmax(0,1fr)_390px]' : 'min-h-[620px] xl:grid-cols-[minmax(0,1fr)_340px]'}`}>
              <div className={`relative min-h-0 overflow-hidden p-3 ${immersiveTopology ? 'bg-[#020617]' : 'bg-slate-950'}`}>
                <div className="absolute left-4 top-4 z-10 rounded-lg border border-white/10 bg-slate-950/80 px-3 py-2 text-[11px] text-slate-300 backdrop-blur">
                  <b className="text-white">{VIEW_DEFS.find((view) => view.id === activeView)?.label}</b>
                  {' · '}{immersiveTopology ? 'Immersive' : 'Standard'}
                  {' · '}drag empty canvas to pan
                  {' · '}zoom {Math.round(graphZoom * 100)}%
                  {isFullscreen ? ' · full-screen lane capacity' : ''}
                  {showLakeOverlay && lakeEnrichment ? ` · historical lake ${lakeEnrichment.lookbackDays}d` : ''}
                </div>
                <svg
                  viewBox={viewBox}
                  className={isFullscreen ? 'h-full min-h-[500px] w-full select-none' : 'h-[600px] w-full select-none'}
                  role="img"
                  aria-label={`${activeView} Forward Operating Base topology`}
                  onPointerDown={onGraphPointerDown}
                  onPointerMove={onGraphPointerMove}
                  onPointerUp={onGraphPointerUp}
                  onPointerCancel={onGraphPointerUp}
                  style={{ cursor: 'grab', touchAction: 'none' }}
                >
                  <defs>
                    <filter id="fobGlow"><feGaussianBlur stdDeviation={immersiveTopology ? '4.5' : '3'} result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                    <filter id="fobSoftGlow"><feGaussianBlur stdDeviation="7" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                    <linearGradient id="fobImmersiveBg" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0%" stopColor="#020617" />
                      <stop offset="48%" stopColor="#07152d" />
                      <stop offset="100%" stopColor="#120a2e" />
                    </linearGradient>
                    <radialGradient id="fobImmersiveHalo">
                      <stop offset="0%" stopColor="rgba(34,211,238,.18)" />
                      <stop offset="55%" stopColor="rgba(99,102,241,.06)" />
                      <stop offset="100%" stopColor="rgba(2,6,23,0)" />
                    </radialGradient>
                    <pattern id="fobGrid" width="46" height="46" patternUnits="userSpaceOnUse">
                      <path d="M46 0H0V46" fill="none" stroke="rgba(56,189,248,.10)" strokeWidth="0.7" />
                    </pattern>
                    <marker id="fobArrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L8,3 z" fill="rgba(148,163,184,.68)" /></marker>
                    <marker id="fobLakeArrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L8,3 z" fill="rgba(167,139,250,.75)" /></marker>
                  </defs>

                  {immersiveTopology ? <>
                    <rect x="-800" y="-800" width={layout.width + 1600} height={layout.height + 1600} fill="url(#fobImmersiveBg)" />
                    <ellipse cx={layout.width * 0.55} cy={layout.height * 0.45} rx={layout.width * 0.48} ry={layout.height * 0.58} fill="url(#fobImmersiveHalo)" />
                    <rect x="-800" y="-800" width={layout.width + 1600} height={layout.height + 1600} fill="url(#fobGrid)" opacity="0.85" />
                    {IMMERSIVE_STARS.map((star, index) => <circle key={`star-${index}`} cx={star.x} cy={star.y} r={star.r} fill="white" opacity={star.opacity} />)}
                    <ellipse cx={layout.width / 2} cy={layout.height / 2} rx={layout.width * 0.43} ry={layout.height * 0.42} fill="none" stroke="rgba(139,92,246,.24)" strokeWidth="2" strokeDasharray="3 14" />
                  </> : <rect x="-500" y="-500" width={layout.width + 1000} height={layout.height + 1000} fill="rgb(2,6,23)" />}

                  {visibleEdges.map(({ edge, from, to }) => {
                    const selected = Boolean(selectedNodeKey && (edge.from === selectedNodeKey || edge.to === selectedNodeKey));
                    const aiC2Highlighted = Boolean(aiC2Overlay && canViewAiC2Overlay && aiC2OverlayNodeKeys.has(edge.from) && aiC2OverlayNodeKeys.has(edge.to));
                    const historical = isHistoricalEdge(edge);
                    const base = edgeVisual(edge, selected);
                    const visual = historical && !selected
                      ? { stroke: 'rgba(167,139,250,.64)', width: immersiveTopology ? 1.7 : 1.4, dash: '5 7' }
                      : base;
                    const path = topologyEdgePath(from, to, immersiveTopology);
                    const animated = immersiveTopology && (selected || isActiveThreatEdge(edge));
                    return <g key={edge.key} pointerEvents="none">
                      {immersiveTopology ? <path d={path} fill="none" stroke={historical ? 'rgba(139,92,246,.12)' : 'rgba(34,211,238,.09)'} strokeWidth={visual.width + 5} strokeLinecap="round" /> : null}
                      <path d={path} fill="none" stroke={visual.stroke} strokeWidth={visual.width} strokeDasharray={visual.dash} strokeLinecap="round" markerEnd={historical ? 'url(#fobLakeArrow)' : 'url(#fobArrow)'} />
                      {aiC2Highlighted ? <path d={path} fill="none" stroke="rgba(244,114,182,.92)" strokeWidth={Math.max(2.2, visual.width + 1)} strokeDasharray="4 8" strokeLinecap="round">
                        <animate attributeName="stroke-dashoffset" values="0;-48" dur="1.6s" repeatCount="indefinite" />
                      </path> : null}
                      {animated ? <path d={path} fill="none" stroke={selected ? 'rgba(103,232,249,.92)' : 'rgba(255,255,255,.34)'} strokeWidth={Math.max(1, visual.width * 0.55)} strokeDasharray="2 13" strokeLinecap="round">
                        <animate attributeName="stroke-dashoffset" values="0;-45" dur={selected ? '1.8s' : '3.2s'} repeatCount="indefinite" />
                      </path> : null}
                    </g>;
                  })}

                  {layout.nodes.map((node) => {
                    const selected = node.key === selectedNodeKey;
                    const aiC2Highlighted = Boolean(aiC2Overlay && canViewAiC2Overlay && aiC2OverlayNodeKeys.has(node.key));
                    const aiC2Status = aiC2OverlayStatusByKey.get(node.key) ?? 'investigating';
                    const visual = nodeVisual(node, selected, canViewRisk);
                    const historicalOnly = Boolean(node.properties.historicalOnly);
                    return <g
                      key={node.key}
                      data-fob-node="true"
                      transform={`translate(${node.x - node.width / 2} ${node.y - node.height / 2})`}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => void selectNode(node.key)}
                      style={{ cursor: 'pointer' }}
                      filter={selected || aiC2Highlighted || (canViewRisk && node.riskScore >= 90) || (immersiveTopology && node.riskScore >= 70) ? 'url(#fobGlow)' : undefined}
                    >
                      {immersiveTopology ? <rect x="-4" y="-4" width={node.width + 8} height={node.height + 8} rx="14" fill={selected ? 'rgba(34,211,238,.08)' : historicalOnly ? 'rgba(139,92,246,.07)' : 'rgba(59,130,246,.035)'} stroke={historicalOnly ? 'rgba(167,139,250,.18)' : 'rgba(56,189,248,.10)'} strokeWidth="1" /> : null}
                      {aiC2Highlighted ? <rect x="-6" y="-6" width={node.width + 12} height={node.height + 12} rx="15" fill={aiC2Status === 'confirmed_compromise' ? 'rgba(244,63,94,.08)' : 'rgba(217,70,239,.06)'} stroke={aiC2Status === 'confirmed_compromise' ? 'rgba(251,113,133,.95)' : 'rgba(232,121,249,.92)'} strokeWidth="2.2" strokeDasharray="5 4">
                        <animate attributeName="stroke-dashoffset" values="0;-36" dur="1.5s" repeatCount="indefinite" />
                      </rect> : null}
                      <rect width={node.width} height={node.height} rx={immersiveTopology ? '12' : '10'} fill={historicalOnly && !selected ? 'rgba(30,27,75,.88)' : visual.fill} stroke={historicalOnly && !selected ? 'rgba(167,139,250,.68)' : visual.stroke} strokeWidth={historicalOnly ? Math.max(1.4, visual.strokeWidth) : visual.strokeWidth} strokeDasharray={historicalOnly ? '5 3' : undefined} />
                      <text x="9" y="15" fill={historicalOnly ? 'rgb(196,181,253)' : 'rgb(148,163,184)'} fontSize="8.5" fontWeight="700">{node.type.replaceAll('_', ' ').toUpperCase().slice(0, 20)}</text>
                      <text x="9" y="31" fill="rgb(226,232,240)" fontSize="10.5" fontWeight="700">{node.label.length > 20 ? `${node.label.slice(0, 19)}…` : node.label}</text>
                      {historicalOnly ? <text x={node.width - 9} y="31" textAnchor="end" fill="rgb(196,181,253)" fontSize="7.5" fontWeight="800">LAKE</text> : null}
                      {aiC2Highlighted ? <text x={node.width - 9} y={historicalOnly ? 43 : 31} textAnchor="end" fill={aiC2Status === 'confirmed_compromise' ? 'rgb(251,113,133)' : 'rgb(232,121,249)'} fontSize="7.5" fontWeight="900">AI C2</text> : null}
                      {canViewRisk && node.riskScore > 0 ? <text x={node.width - 9} y="15" textAnchor="end" fill={node.riskScore >= 70 ? 'rgb(253,186,116)' : 'rgb(203,213,225)'} fontSize="8.5" fontWeight="800">R{node.riskScore}</text> : null}
                    </g>;
                  })}
                </svg>
                <div className="absolute bottom-4 left-4 z-10 rounded-lg border border-white/10 bg-slate-950/80 px-3 py-2 text-[11px] text-slate-400 backdrop-blur">
                  Rendered {layout.nodes.length} nodes / {layout.edges.length} edges{layout.clipped ? ` · ${layout.clipped} lower-priority nodes summarized out of canvas` : ''}.
                  {' '}Drag canvas = pan · Reset returns center/100% · historical Data Lake edges are violet/dashed.
                </div>
                {immersiveTopology ? <div className="absolute bottom-4 right-4 z-10 rounded-lg border border-violet-400/20 bg-violet-950/40 px-3 py-2 text-[10px] font-black uppercase tracking-[0.16em] text-violet-200 backdrop-blur">Immersive topology · evidence paths in motion</div> : null}
              </div>

              <aside className={`border-l border-border bg-background p-4 ${isFullscreen ? 'min-h-0 overflow-y-auto' : ''}`}>
                {selectedNode ? <NodeDossier node={selectedNode} evidence={nodeEvidence} evidenceLoading={evidenceLoading} showRisk={canViewRisk} /> : <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center"><Eye size={28} className="text-muted-foreground" /><div className="mt-3 font-bold text-foreground">Select a terrain node</div><p className="mt-2 text-xs text-muted-foreground">Inspect normalized properties, source tables, observation timestamps, risk signals and evidence lineage.</p></div>}
              </aside>
            </div>
          ) : null}

          {activeView === 'timeline' ? <TimelineDeltaView delta={delta} loading={deltaLoading} snapshots={snapshots} /> : null}
          {activeView === 'sources' ? <SourceHealthView sources={snapshot.sources} diagnostics={snapshot.diagnostics} lakeEnrichment={canViewLake ? lakeEnrichment : null} /> : null}
          {activeView === 'evidence' ? <EvidenceView evidence={evidence} loading={evidenceLoading} /> : null}
        </section>

        {snapshot.diagnostics.length ? <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-4 text-xs text-amber-100"><div className="font-black">Terrain scan diagnostics</div><ul className="mt-2 list-disc space-y-1 pl-5">{snapshot.diagnostics.slice(0, 16).map((item, idx) => <li key={`${idx}-${item}`}>{item}</li>)}</ul></div> : null}
      </> : null}
    </div>
  );
}

function LayerToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return <button type="button" onClick={onClick} className={`rounded-full border px-3 py-1.5 font-bold ${active ? 'border-cyan-400/40 bg-cyan-500/15 text-cyan-100' : 'border-border text-muted-foreground hover:bg-muted'}`}>{label}</button>;
}

function Metric({ label, value, helper, icon: Icon }: { label: string; value: string; helper: string; icon: typeof Network }) {
  return <div className="rounded-xl border border-border bg-card p-4 shadow-sm"><div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</div><Icon size={16} className="text-primary" /></div><div className="mt-2 text-2xl font-black text-foreground">{value}</div><div className="mt-1 text-[11px] text-muted-foreground">{helper}</div></div>;
}

function NodeDossier({ node, evidence, evidenceLoading, showRisk }: { node: FobNode; evidence: FobEvidence[]; evidenceLoading: boolean; showRisk: boolean }) {
  const Icon = nodeIcon(node.type);
  const riskSignals = Array.isArray(node.properties.riskSignals) ? node.properties.riskSignals.map(String) : [];
  return <div className="space-y-4">
    <div><div className={`flex items-center gap-2 text-xs font-black uppercase tracking-wide ${nodeTone(node.type)}`}><Icon size={15} />{node.type.replaceAll('_', ' ')}</div><div className="mt-2 break-all text-lg font-black text-foreground">{node.label}</div><div className="mt-1 break-all font-mono text-[10px] text-muted-foreground">{node.key}</div></div>
    <div className="grid grid-cols-2 gap-2 text-xs"><Detail label="First seen" value={formatTime(node.firstSeen)} /><Detail label="Last seen" value={formatTime(node.lastSeen)} /><Detail label="Sources" value={node.sourceTables.join(', ') || '—'} wide />{showRisk ? <Detail label="Risk marker" value={`${node.riskScore}/100`} /> : null}</div>
    {showRisk && riskSignals.length ? <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-3"><div className="text-xs font-black uppercase tracking-wide text-amber-200">Deterministic risk signals</div><ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] text-amber-100">{riskSignals.slice(0, 8).map((signal, index) => <li key={`${index}-${signal}`}>{signal}</li>)}</ul></div> : null}
    <div><div className="text-xs font-black uppercase tracking-wide text-muted-foreground">Normalized properties</div><div className="mt-2 space-y-1">{safePropertyEntries(node.properties).filter(([key]) => key !== 'riskSignals').map(([key, value]) => <div key={key} className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs"><div className="text-[10px] uppercase text-muted-foreground">{key}</div><div className="mt-0.5 break-all text-foreground">{value}</div></div>)}{safePropertyEntries(node.properties).length === 0 ? <div className="text-xs text-muted-foreground">No additional normalized properties.</div> : null}</div></div>
    <div><div className="flex items-center justify-between"><div className="text-xs font-black uppercase tracking-wide text-muted-foreground">Evidence lineage</div>{evidenceLoading ? <RefreshCw size={13} className="animate-spin text-muted-foreground" /> : null}</div><div className="mt-2 space-y-2">{evidence.slice(0, 10).map((item) => <div key={item.id} className="rounded-lg border border-border bg-muted/20 p-2 text-[11px]"><div className="font-bold text-foreground">{item.sourceTable} · {item.evidenceType}</div><div className="mt-1 text-muted-foreground">{formatTime(item.observedAt)} · {item.queryName}</div></div>)}{!evidenceLoading && evidence.length === 0 ? <div className="text-xs text-muted-foreground">No row-level evidence is stored for this node. Root/configuration nodes may be derived from Customer Master.</div> : null}</div></div>
  </div>;
}

function Detail({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return <div className={`rounded-lg border border-border bg-muted/20 px-3 py-2 ${wide ? 'col-span-2' : ''}`}><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="mt-1 break-all font-semibold text-foreground">{value}</div></div>;
}

function TimelineDeltaView({ delta, loading, snapshots }: { delta: FobDelta | null; loading: boolean; snapshots: FobSnapshotSummary[] }) {
  const threatRows = delta ? [
    ['Open incidents', delta.threats.incidents],
    ['Security alerts', delta.threats.alerts],
    ['Active IoCs', delta.threats.iocs],
    ['IoC sightings', delta.threats.iocSightings],
    ['CVEs', delta.threats.cves],
    ['Vulnerable devices', delta.threats.vulnerableDevices],
    ['High-risk nodes', delta.threats.highRiskNodes],
    ['Critical-risk nodes', delta.threats.criticalRiskNodes],
  ].filter((row): row is [string, ThreatDelta] => Boolean(row[1])) : [];

  return <div className="p-5">
    <div className="flex items-start justify-between gap-4"><div><h3 className="text-lg font-black text-foreground">Timeline & Graph Delta</h3><p className="mt-1 text-sm text-muted-foreground">Deterministic comparison of this persisted terrain snapshot with the immediately preceding snapshot for the same customer.</p></div>{loading ? <RefreshCw size={20} className="animate-spin text-primary" /> : <Clock3 size={20} className="text-primary" />}</div>
    {!loading && delta && !delta.hasBaseline ? <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-500/10 p-4 text-sm text-cyan-100">This is the first retained snapshot for the customer, so there is no earlier graph baseline to compare yet.</div> : null}
    {delta?.hasBaseline ? <>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <DeltaMetric label="Nodes" value={signed(delta.nodes.net)} helper={`${delta.nodes.added} added · ${delta.nodes.removed} removed`} />
        <DeltaMetric label="Relationships" value={signed(delta.edges.net)} helper={`${delta.edges.added} added · ${delta.edges.removed} removed`} />
        <DeltaMetric label="Risk escalations" value={String(delta.risk.increased)} helper={`${delta.risk.decreased} nodes decreased`} />
        <DeltaMetric label="Baseline" value={delta.previousSnapshotId?.slice(0, 8) ?? '—'} helper={formatTime(delta.previousGeneratedAt)} />
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <div className="rounded-xl border border-border bg-background p-4"><div className="text-sm font-black text-foreground">Threat signal delta</div><div className="mt-3 grid gap-2 sm:grid-cols-2">{threatRows.map(([label, value]) => <div key={label} className="rounded-lg border border-border bg-muted/20 p-3"><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="mt-1 flex items-end justify-between"><span className="text-lg font-black text-foreground">{value.current}</span><span className={`text-xs font-bold ${value.delta > 0 ? 'text-rose-300' : value.delta < 0 ? 'text-emerald-300' : 'text-muted-foreground'}`}>{signed(value.delta)}</span></div><div className="text-[10px] text-muted-foreground">previous {value.previous}</div></div>)}</div></div>
        <div className="rounded-xl border border-border bg-background p-4"><div className="text-sm font-black text-foreground">Top risk escalations</div><div className="mt-3 max-h-[330px] space-y-2 overflow-auto">{delta.risk.topEscalations.slice(0, 16).map((row) => <div key={row.key} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs"><div className="min-w-0"><div className="truncate font-bold text-foreground">{row.label}</div><div className="text-[10px] uppercase text-muted-foreground">{row.type.replaceAll('_', ' ')}</div></div><div className="whitespace-nowrap font-black text-rose-300">{row.from} → {row.to}</div></div>)}{delta.risk.topEscalations.length === 0 ? <div className="text-xs text-muted-foreground">No node risk scores increased between these snapshots.</div> : null}</div></div>
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-2"><DeltaNodeList title="Newly observed nodes" rows={delta.topAdded} empty="No new nodes were added." /><DeltaNodeList title="No longer present in snapshot" rows={delta.topRemoved} empty="No nodes were removed." /></div>
    </> : null}
    {snapshots.length ? <div className="mt-5 rounded-xl border border-border bg-background p-4"><div className="text-sm font-black text-foreground">Recent terrain timeline</div><div className="mt-3 space-y-2">{snapshots.slice(0, 10).map((item) => <div key={item.id} className="grid gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs md:grid-cols-[190px_90px_1fr]"><span className="text-foreground">{formatTime(item.generatedAt)}</span><span className={item.status === 'succeeded' ? 'text-emerald-300' : 'text-amber-300'}>{item.status}</span><span className="text-muted-foreground">{n(item.summary.nodes)} nodes · {n(item.summary.edges)} edges · {n(item.summary.highRiskNodes)} high-risk · {n(item.summary.incidents)} incidents · {n(item.summary.iocSightings)} IoC sightings</span></div>)}</div></div> : null}
  </div>;
}

function DeltaMetric({ label, value, helper }: { label: string; value: string; helper: string }) {
  return <div className="rounded-xl border border-border bg-background p-4"><div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground">{label}</div><div className="mt-1 text-xl font-black text-foreground">{value}</div><div className="mt-1 text-[11px] text-muted-foreground">{helper}</div></div>;
}

function DeltaNodeList({ title, rows, empty }: { title: string; rows: DeltaNode[]; empty: string }) {
  return <div className="rounded-xl border border-border bg-background p-4"><div className="text-sm font-black text-foreground">{title}</div><div className="mt-3 max-h-[300px] space-y-2 overflow-auto">{rows.slice(0, 18).map((row) => <div key={row.key} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs"><div className="min-w-0"><div className="truncate font-bold text-foreground">{row.label}</div><div className="text-[10px] uppercase text-muted-foreground">{row.type.replaceAll('_', ' ')}</div></div><div className="whitespace-nowrap text-[11px] font-bold text-muted-foreground">R{row.riskScore}</div></div>)}{rows.length === 0 ? <div className="text-xs text-muted-foreground">{empty}</div> : null}</div></div>;
}

function SourceHealthView({ sources, diagnostics, lakeEnrichment }: { sources: FobSourceHealth[]; diagnostics: string[]; lakeEnrichment: FobLakeEnrichment | null }) {
  return <div className="p-5">
    <div className="flex items-start justify-between gap-4"><div><h3 className="text-lg font-black text-foreground">Source Health & Table Availability</h3><p className="mt-1 text-sm text-muted-foreground">Stage 1C keeps the hot/current terrain scan on Analytics-tier sources and exposes Microsoft Sentinel Data Lake as a separate, explicit historical enrichment action so slower/billable lake queries never block the live terrain refresh.</p></div><TableProperties size={22} className="text-primary" /></div>
    <div className="mt-4 overflow-hidden rounded-xl border border-border"><div className="overflow-x-auto"><table className="w-full text-left text-xs"><thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2">Source</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Rows</th><th className="px-3 py-2">Last seen</th><th className="px-3 py-2">Message</th></tr></thead><tbody>{sources.map((source) => <tr key={source.sourceKey} className="border-t border-border"><td className="px-3 py-2 font-bold text-foreground">{source.displayName}<div className="font-mono text-[10px] text-muted-foreground">{source.sourceKey}</div></td><td className="px-3 py-2 text-muted-foreground">{source.category}</td><td className="px-3 py-2"><span className={`rounded-full border px-2 py-1 ${statusTone(source.status)}`}>{source.status.replaceAll('_', ' ')}</span></td><td className="px-3 py-2 text-right text-foreground">{compact(source.rowCount)}</td><td className="px-3 py-2 text-muted-foreground">{formatTime(source.lastSeen)}</td><td className="max-w-lg px-3 py-2 text-muted-foreground">{source.message}</td></tr>)}</tbody></table></div></div>
    {lakeEnrichment ? <div className="mt-5 rounded-xl border border-violet-400/20 bg-violet-500/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2"><div><div className="text-sm font-black text-violet-100">Historical Sentinel Data Lake enrichment</div><div className="mt-1 text-[11px] text-muted-foreground">{lakeEnrichment.lookbackDays}d historical horizon · excludes the most recent {lakeEnrichment.baselineLookbackHours}h already represented by the snapshot · generated {formatTime(lakeEnrichment.generatedAt)}</div></div><span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase ${statusTone(lakeEnrichment.status)}`}>{lakeEnrichment.status}</span></div>
      <div className="mt-3 overflow-hidden rounded-lg border border-border"><table className="w-full text-left text-xs"><thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2">Lake source</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Aggregates</th><th className="px-3 py-2 text-right">Latency</th><th className="px-3 py-2">Message</th></tr></thead><tbody>{lakeEnrichment.sources.map((source) => <tr key={`lake-${source.sourceKey}`} className="border-t border-border"><td className="px-3 py-2 font-bold text-foreground">{source.displayName}<div className="font-mono text-[10px] text-violet-300">{source.sourceKey}</div></td><td className="px-3 py-2"><span className={`rounded-full border px-2 py-1 ${statusTone(source.status === 'no_data' ? 'no_recent_data' : source.status)}`}>{source.status.replaceAll('_', ' ')}</span></td><td className="px-3 py-2 text-right text-foreground">{compact(source.rowCount)}</td><td className="px-3 py-2 text-right text-muted-foreground">{source.latencyMs == null ? '—' : `${Math.round(source.latencyMs / 1000)}s`}</td><td className="max-w-lg px-3 py-2 text-muted-foreground">{source.message}</td></tr>)}</tbody></table></div>
      {lakeEnrichment.diagnostics.length ? <div className="mt-3 text-[11px] text-amber-200">{lakeEnrichment.diagnostics.slice(0, 6).join(' · ')}</div> : null}
    </div> : null}
    {diagnostics.length ? <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-500/10 p-3 text-xs text-amber-100"><b>Partial-source diagnostics:</b><ul className="mt-2 list-disc space-y-1 pl-5">{diagnostics.slice(0, 16).map((item, idx) => <li key={`${idx}-${item}`}>{item}</li>)}</ul></div> : null}
  </div>;
}

function EvidenceView({ evidence, loading }: { evidence: FobEvidence[]; loading: boolean }) {
  return <div className="p-5">
    <div className="flex items-start justify-between gap-4"><div><h3 className="text-lg font-black text-foreground">Evidence Lineage</h3><p className="mt-1 text-sm text-muted-foreground">Terrain, Stage 1B threat observations and persisted Stage 1C Data Lake enrichment retain their originating telemetry table, bounded query purpose, observation time and normalized payload.</p></div>{loading ? <RefreshCw size={20} className="animate-spin text-primary" /> : <GitBranch size={20} className="text-primary" />}</div>
    <div className="mt-4 overflow-hidden rounded-xl border border-border"><div className="max-h-[580px] overflow-auto"><table className="w-full text-left text-xs"><thead className="sticky top-0 bg-muted text-muted-foreground"><tr><th className="px-3 py-2">Observed</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Evidence key</th><th className="px-3 py-2">Query</th><th className="px-3 py-2">Normalized payload</th></tr></thead><tbody>{evidence.map((item) => <tr key={item.id} className="border-t border-border align-top"><td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{formatTime(item.observedAt)}</td><td className="px-3 py-2 font-bold text-foreground">{item.sourceTable}</td><td className="px-3 py-2 text-muted-foreground">{item.evidenceType}</td><td className="max-w-xs break-all px-3 py-2 font-mono text-[10px] text-muted-foreground">{item.evidenceKey}</td><td className="px-3 py-2 text-muted-foreground">{item.queryName}</td><td className="max-w-md break-all px-3 py-2 font-mono text-[10px] text-muted-foreground">{JSON.stringify(item.payload)}</td></tr>)}</tbody></table>{!loading && evidence.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No row-level evidence stored for this snapshot.</div> : null}</div></div>
  </div>;
}
